import { Controller } from "@hotwired/stimulus"
import * as htmlToImage from "html-to-image"

// Snip controller for the album export page.
// Captures the #capture element as a PNG using html-to-image,
// then copies to clipboard or downloads as a file.
// Hides UI elements (checkboxes, ratings, handles) during capture.

const HIDE_SELECTORS = [
  'input[type="checkbox"]',
  '[data-sort-handle]',
  '.star-rating',
  '.rating',
  '[data-action*="checkbox"]',
  '.toolbar'
]

export default class extends Controller {
  static targets = ["capture"]

  async copy(event) {
    await this._snip(event.currentTarget, "clipboard")
  }

  async download(event) {
    await this._snip(event.currentTarget, "download")
  }

  async _snip(btn, mode) {
    const capture = this.hasCaptureTarget ? this.captureTarget : document.getElementById("capture")
    if (!capture) return

    const origText = btn.textContent
    btn.textContent = "Capturing..."
    btn.disabled = true

    try {
      // Wait for all images to load
      const imgs = capture.querySelectorAll("img")
      await Promise.all(Array.from(imgs).map(img => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve()
        return new Promise(r => { img.onload = r; img.onerror = r })
      }))

      // Hide UI elements for clean capture
      this._hideUI()

      const blob = await htmlToImage.toBlob(capture, {
        skipFonts: true,
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: null
      })

      // Restore UI elements
      this._showUI()

      if (!blob) throw new Error("Failed to create image blob")

      if (mode === "clipboard") {
        try {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
          btn.textContent = "Copied!"
          btn.classList.add("success")
        } catch {
          this._downloadBlob(blob)
          btn.textContent = "Downloaded!"
        }
      } else {
        this._downloadBlob(blob)
        btn.textContent = "Downloaded!"
      }
    } catch (e) {
      console.error("Snip failed:", e)
      this._showUI()
      btn.textContent = "Failed"
    } finally {
      btn.disabled = false
      setTimeout(() => { btn.textContent = origText; btn.classList.remove("success") }, 2000)
    }
  }

  _hideUI() {
    HIDE_SELECTORS.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => el.dataset.snipHidden = el.style.display || "")
      document.querySelectorAll(sel).forEach(el => el.style.display = "none")
    })
  }

  _showUI() {
    document.querySelectorAll("[data-snip-hidden]").forEach(el => {
      el.style.display = el.dataset.snipHidden
      delete el.dataset.snipHidden
    })
  }

  _downloadBlob(blob) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.download = document.title.replace(/[^a-zA-Z0-9_-]/g, "_") + ".png"
    a.href = url
    a.click()
    URL.revokeObjectURL(url)
  }
}
